import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { DisplayUserDto } from '../../shared/models/display-user.dto';
import { labelsByProfileType, ProfileType } from '../../shared/models/enums/profile-type.enum';
import { Pagination } from '../../shared/pagination/pagination';
import { PaginationOptions } from '../../shared/pagination/pagination-options';
import { UserService } from '../../shared/services/user.service';
import { SortDirection, SortEvent } from '../../shared/sortable/sort-event';
import { SortableDirective } from '../../shared/sortable/sortable.directive';
import { ToastService } from '../../shared/toast/toast.service';
import { UserDetailsComponent } from './user-details/user-details.component';

@Component({
  selector: 'app-admin-users',
  templateUrl: './users.component.html',
})
export class UsersComponent implements OnInit, OnDestroy {
  private static readonly resetUsers: Pagination<DisplayUserDto> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));
  @ViewChildren(SortableDirective)
  public headers: QueryList<SortableDirective>;
  public users: Pagination<DisplayUserDto> = UsersComponent.resetUsers;
  public readonly profileTypes: Map<ProfileType, string> = labelsByProfileType;
  public searchForm: FormGroup<{ search: FormControl<string> }>;
  public submitted: boolean = false;

  private userProfileCreatedSubscription: Subscription;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.searchForm = this.formBuilder.group({
      search: [null],
    });

    this.userProfileCreatedSubscription = this.userService.userProfileAction$.subscribe((res: { action: 'created' | 'updated' }) => {
      switch (res.action) {
        case 'created':
          this.toastService.show("L'utilisateur a été ajouté avec succès.", 'success');
          break;
        case 'updated':
          this.toastService.show("L'utilisateur a été modifié avec succès.", 'success');
          break;
        default:
          break;
      }
      this.update();
    });
    await this.update();
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.searchForm.valid) {
      this.users = await this.userService.searchUsers(this.searchForm.controls.search.value, this.users.options);
    }
  }

  public ngOnDestroy(): void {
    if (this.userProfileCreatedSubscription) {
      this.userProfileCreatedSubscription.unsubscribe();
    }
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    this.users = await this.userService.searchUsers(this.searchForm.controls.search.value, this.users.options.withSort(column, direction === 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      this.users = await this.userService.searchUsers(this.searchForm.controls.search.value, this.users.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event && event.target && event.target.value) {
      const limit: number = event.target.value;
      this.users = await this.userService.searchUsers(this.searchForm.controls.search.value, this.users.options.withLimit(limit));
    }
  }

  public async openModal(userId?: number): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(UserDetailsComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: UserDetailsComponent = modal.componentInstance as UserDetailsComponent;
    modalComponent.onInit(userId);
  }

  private async update(): Promise<void> {
    this.users = await this.userService.searchUsers(this.searchForm.controls.search.value, this.users.options);
  }

  public async onExportToCsv(): Promise<void> {
    return this.userService.getUsersToCsv(this.searchForm.controls.search.value);
  }
}
